import { userSelector } from '@/pages/user/selectors'
import { useAppSelector } from './useRedux'
import { GroupedEntitlement } from '@/constants/UiPropertyKeys'
import { Subscription } from '@/types/Auth'

/**
 * Check if user has a specific entitlement in a specific group (exact match)
 */
export function userHasEntitlement(
  subscriptions: Subscription[],
  group: string,
  entitlement: string
): boolean {
  return subscriptions.some((sub) => {
    const groupEntitlements = sub.entitlements[group]
    return groupEntitlements?.includes(entitlement) ?? false
  })
}

/**
 * Check if user has ALL required grouped entitlements (exact match for each)
 */
export function userHasAllGroupedEntitlements(
  subscriptions: Subscription[],
  required: GroupedEntitlement[]
): boolean {
  if (required.length === 0) return false
  return required.every((req) => userHasEntitlement(subscriptions, req.group, req.entitlement))
}

/**
 * Original hook for backward compatibility - checks for any PREMIUM entitlement (partial match)
 */
export function useHasActiveSubscription(): boolean {
  const { subscriptions } = useAppSelector(userSelector)
  return subscriptions.some((s) =>
    Object.values(s.entitlements)
      .flat()
      .some((e) => e.includes('PREMIUM'))
  )
}

/**
 * Hook to check if user has all required entitlements
 * Falls back to PREMIUM partial-match check if no specific entitlements are required
 */
export function useHasAllRequiredEntitlements(
  requiredEntitlements: GroupedEntitlement[] | null | undefined
): boolean {
  const { subscriptions } = useAppSelector(userSelector)

  // Fallback: if no specific entitlements required, use existing PREMIUM check
  if (!requiredEntitlements || requiredEntitlements.length === 0) {
    return subscriptions.some((s) =>
      Object.values(s.entitlements)
        .flat()
        .some((e) => e.includes('PREMIUM'))
    )
  }

  // Exact group.entitlement matching
  return userHasAllGroupedEntitlements(subscriptions, requiredEntitlements)
}
